import React, { useRef, useState, useMemo, useCallback } from 'react';
import { 
  UploadCloud, 
  AlertCircle, 
  Plus, 
  Database, 
  Sparkles, 
  FileSpreadsheet, 
  Download, 
  FileDown, 
  X 
} from 'lucide-react';
import { useSaaSContext } from '../../store/SaaSContext';
import { useToast } from '../../store/ToastContext';
import { validateCsvData } from '../../utils/csvValidation';
import { parseDelimitedText, ParsedCsvResult } from '../../utils/csvParser';
import { 
  generateTemplateAlignedCsv, 
  TEMPLATE_PRESETS, 
  formatCsvString, 
  triggerCsvDownload,
  CsvTemplatePreset 
} from '../../utils/csvTemplateGenerator';
import { CsvValidationPanel, FormatMeta } from './CsvValidationPanel';
import { InAppVariableGeneratorPanel } from './InAppVariableGeneratorPanel';
import { MacWindow } from './MacWindow';
import { CSVData } from '../../types';

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.csv', '.tsv', '.tab', '.txt'];

const SAMPLE_DATASETS: { name: string; description: string; data: CSVData; formatMeta: FormatMeta }[] = [
  {
    name: 'E-Commerce Product Barcodes',
    description: 'SKUs, product titles, EAN-13 barcodes, prices, and QR links',
    data: {
      headers: ['SKU', 'ProductName', 'Price', 'Barcode', 'Category', 'QrLink'],
      rows: [
        { SKU: 'PROD-101', ProductName: 'Wireless Noise-Canceling Headphones', Price: '79.99', Barcode: '8901234567891', Category: 'Electronics', QrLink: 'https://store.example.com/p/101' },
        { SKU: 'PROD-102', ProductName: 'Ergonomic Memory Foam Cushion', Price: '34.50', Barcode: '8901234567907', Category: 'Office', QrLink: 'https://store.example.com/p/102' },
        { SKU: 'PROD-103', ProductName: 'Stainless Steel Thermal Tumbler', Price: '24.95', Barcode: '8901234567914', Category: 'Home', QrLink: 'https://store.example.com/p/103' },
        { SKU: 'PROD-104', ProductName: 'Smart Fitness Tracker Band', Price: '49.00', Barcode: '8901234567921', Category: 'Fitness', QrLink: 'https://store.example.com/p/104' },
        { SKU: 'PROD-105', ProductName: 'Braided Fast-Charging USB-C Cable', Price: '12.99', Barcode: '8901234567938', Category: 'Electronics', QrLink: 'https://store.example.com/p/105' },
        { SKU: 'PROD-106', ProductName: 'Organic Green Tea Blend 100g', Price: '18.50', Barcode: '8901234567945', Category: 'Grocery', QrLink: 'https://store.example.com/p/106' },
      ]
    },
    formatMeta: {
      delimiter: ',',
      delimiterName: 'Comma (,)',
      headerRowIndex: 0,
      hasHeader: true,
      confidence: 'high',
      skippedPreambleCount: 0,
      headerDetectionReason: 'Auto-detected standard comma-separated columns with Line 1 headers',
      delimiterCandidates: [
        { char: ',', name: 'Comma (,)', count: 35, avgCols: 6, score: 9.8, consistency: 0, lineParticipation: 100, isProbable: true },
        { char: '\t', name: 'Tab (\\t)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
        { char: ';', name: 'Semicolon (;)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
        { char: '|', name: 'Pipe (|)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false }
      ]
    }
  },
  {
    name: 'Event Attendee Passes',
    description: 'Names, companies, access levels, VIP badges, and QR codes',
    data: {
      headers: ['AttendeeName', 'Company', 'Role', 'AccessTier', 'BadgeID', 'QrCode'],
      rows: [
        { AttendeeName: 'Alex Morgan', Company: 'Apex Global', Role: 'Keynote Speaker', AccessTier: 'VIP All-Access', BadgeID: 'VIP-001', QrCode: 'https://event.io/pass/VIP-001' },
        { AttendeeName: 'Sarah Jenkins', Company: 'Nexus Tech', Role: 'Product Lead', AccessTier: 'Full Conference', BadgeID: 'CONF-102', QrCode: 'https://event.io/pass/CONF-102' },
        { AttendeeName: 'Marcus Vance', Company: 'Horizon Media', Role: 'Director', AccessTier: 'Full Conference', BadgeID: 'CONF-103', QrCode: 'https://event.io/pass/CONF-103' },
        { AttendeeName: 'Elena Rostova', Company: 'BioHealth Labs', Role: 'Researcher', AccessTier: 'Exhibitor', BadgeID: 'EXH-501', QrCode: 'https://event.io/pass/EXH-501' },
        { AttendeeName: 'David Chen', Company: 'Venture Capital Partners', Role: 'Managing Partner', AccessTier: 'VIP All-Access', BadgeID: 'VIP-002', QrCode: 'https://event.io/pass/VIP-002' },
      ]
    },
    formatMeta: {
      delimiter: ',',
      delimiterName: 'Comma (,)',
      headerRowIndex: 0,
      hasHeader: true,
      confidence: 'high',
      skippedPreambleCount: 0,
      headerDetectionReason: 'Auto-detected standard comma-separated columns with Line 1 headers',
      delimiterCandidates: [
        { char: ',', name: 'Comma (,)', count: 30, avgCols: 6, score: 9.8, consistency: 0, lineParticipation: 100, isProbable: true },
        { char: '\t', name: 'Tab (\\t)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
        { char: ';', name: 'Semicolon (;)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
        { char: '|', name: 'Pipe (|)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false }
      ]
    }
  },
  {
    name: 'Industrial Asset & Inventory Tags',
    description: 'Asset numbers, serials, locations, maintenance dates, and tracking URLs',
    data: {
      headers: ['AssetTag', 'EquipmentType', 'Location', 'SerialNumber', 'InspectionDate', 'TrackURL'],
      rows: [
        { AssetTag: 'AST-8001', EquipmentType: 'Hydraulic Press 50T', Location: 'Zone A - Bay 4', SerialNumber: 'SN-99201-HP', InspectionDate: '2026-06-15', TrackURL: 'https://mfg.internal/asset/8001' },
        { AssetTag: 'AST-8002', EquipmentType: 'CNC Milling Center', Location: 'Zone B - Bay 1', SerialNumber: 'SN-99202-CNC', InspectionDate: '2026-07-20', TrackURL: 'https://mfg.internal/asset/8002' },
        { AssetTag: 'AST-8003', EquipmentType: 'Laser Cutting Unit', Location: 'Zone B - Bay 3', SerialNumber: 'SN-99203-LC', InspectionDate: '2026-08-10', TrackURL: 'https://mfg.internal/asset/8003' },
        { AssetTag: 'AST-8004', EquipmentType: 'Air Compressor 20HP', Location: 'Utility Room 2', SerialNumber: 'SN-99204-AC', InspectionDate: '2026-05-30', TrackURL: 'https://mfg.internal/asset/8004' },
      ]
    },
    formatMeta: {
      delimiter: ',',
      delimiterName: 'Comma (,)',
      headerRowIndex: 0,
      hasHeader: true,
      confidence: 'high',
      skippedPreambleCount: 0,
      headerDetectionReason: 'Auto-detected standard comma-separated columns with Line 1 headers',
      delimiterCandidates: [
        { char: ',', name: 'Comma (,)', count: 24, avgCols: 6, score: 9.8, consistency: 0, lineParticipation: 100, isProbable: true },
        { char: '\t', name: 'Tab (\\t)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
        { char: ';', name: 'Semicolon (;)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false },
        { char: '|', name: 'Pipe (|)', count: 0, avgCols: 1, score: 0, consistency: 0, lineParticipation: 0, isProbable: false }
      ]
    }
  }
];

export const DataStep = ({ onNext }: { onNext: () => void }) => {
  const { csvData, setCsvData, template } = useSaaSContext();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // File metadata & formatting options
  const [rawFileText, setRawFileText] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [formatMeta, setFormatMeta] = useState<FormatMeta | null>(null);
  const [selectedDelimiter, setSelectedDelimiter] = useState<string>('auto');
  const [selectedHeaderRow, setSelectedHeaderRow] = useState<number>(-99);
  const [hasHeaderOverride, setHasHeaderOverride] = useState<boolean | undefined>(undefined);
  
  const [showGenerator, setShowGenerator] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Compute live validation report whenever csvData or template changes
  const validationReport = useMemo(() => {
    return validateCsvData(csvData, template);
  }, [csvData, template]);

  const activeTemplatePreset = useMemo(() => {
    return generateTemplateAlignedCsv(template);
  }, [template]);

  const handleDownloadTemplate = useCallback((preset?: CsvTemplatePreset) => {
    const target = preset || activeTemplatePreset;
    const csvContent = formatCsvString(target.headers, target.rows);
    triggerCsvDownload(target.filename, csvContent);
    
    toast.success(
      'Template Downloaded',
      `Saved "${target.filename}" formatted CSV template (${target.headers.length} headers).`,
      { duration: 4000 }
    );
  }, [activeTemplatePreset, toast]);

  const processTextContent = useCallback((
    text: string, 
    name: string, 
    overrideDelimiter?: string, 
    overrideHeaderRow?: number,
    overrideHasHeader?: boolean,
    isInitialUpload = false
  ) => {
    try {
      const delimiterOption = (overrideDelimiter !== undefined ? overrideDelimiter : selectedDelimiter) === 'auto'
        ? undefined
        : (overrideDelimiter !== undefined ? overrideDelimiter : selectedDelimiter);

      const headerRowOption = (overrideHeaderRow !== undefined ? overrideHeaderRow : selectedHeaderRow) === -99
        ? undefined
        : (overrideHeaderRow !== undefined ? overrideHeaderRow : selectedHeaderRow);

      const effectiveHasHeader = overrideHasHeader !== undefined ? overrideHasHeader : hasHeaderOverride;

      const parsed: ParsedCsvResult = parseDelimitedText(text, {
        delimiter: delimiterOption,
        headerRowIndex: headerRowOption,
        hasHeader: effectiveHasHeader
      });

      // 1. Check for fatal syntax errors (e.g. unclosed quotes, broken multi-line tokens)
      if (parsed.syntaxErrors && parsed.syntaxErrors.length > 0) {
        const errorSummary = parsed.syntaxErrors.map(
          e => `Line ${e.row || '?'}: ${e.message}${e.snippet ? ` — "${e.snippet}"` : ''}`
        );

        toast.error(
          'Invalid CSV Syntax Detected',
          `Found ${parsed.syntaxErrors.length} syntax ${parsed.syntaxErrors.length === 1 ? 'error' : 'errors'} in "${name}". Click to inspect line details.`,
          {
            details: errorSummary,
            duration: 8000
          }
        );
      }

      // 2. Check for empty rows / missing dataset
      if (!parsed.csvData.rows || parsed.csvData.rows.length === 0) {
        const msg = 'No valid data records could be extracted from the file. Please check that the file is not empty and uses valid delimiters.';
        setError(msg);
        toast.error(
          'Invalid CSV Data Structure',
          `"${name}" contains no valid tabular data rows.`,
          {
            details: [
              `File: ${name}`,
              `Detected Delimiter: ${parsed.detectedDelimiterName}`,
              `Total Lines Scanned: ${parsed.totalLinesScanned}`,
              'Suggestion: Try downloading a reference template or changing the delimiter setting.'
            ]
          }
        );
        return;
      }

      // 3. Inform of non-fatal syntax warnings if present
      if (parsed.syntaxWarnings && parsed.syntaxWarnings.length > 0 && isInitialUpload) {
        toast.warning(
          'CSV Formatting Notice',
          `Parsed ${parsed.csvData.rows.length} rows, with ${parsed.syntaxWarnings.length} formatting ${parsed.syntaxWarnings.length === 1 ? 'irregularity' : 'irregularities'}.`,
          {
            details: parsed.syntaxWarnings.slice(0, 6).map(w => `Row ${w.row || '?'}: ${w.message}`)
          }
        );
      }

      setRawFileText(text);
      setFileName(name);
      setFormatMeta({
        delimiter: parsed.detectedDelimiter,
        delimiterName: parsed.detectedDelimiterName,
        headerRowIndex: parsed.detectedHeaderRowIndex,
        hasHeader: parsed.hasHeader,
        confidence: parsed.confidence,
        skippedPreambleCount: parsed.skippedPreambleCount,
        headerDetectionReason: parsed.headerDetectionReason,
        delimiterCandidates: parsed.delimiterCandidates,
        rawSampleLines: parsed.rawSampleLines
      });
      setCsvData(parsed.csvData);
      setError(null);

      if (isInitialUpload) {
        toast.success(
          'File Uploaded Successfully',
          `Loaded ${parsed.csvData.rows.length.toLocaleString()} records & ${parsed.csvData.headers.length} columns from "${name}" (${parsed.detectedDelimiterName}).`,
          { duration: 4500 }
        );
      }
    } catch (err: any) {
      const errMsg = err?.message || 'Failed to parse file. Please verify delimiter format and quotes.';
      setError(errMsg);
      toast.error(
        'CSV Parsing Failed',
        errMsg,
        {
          details: [
            `File: ${name}`,
            `Error: ${errMsg}`,
            'Verify that text columns with commas or special characters are wrapped in quotation marks.'
          ]
        }
      );
    }
  }, [selectedDelimiter, selectedHeaderRow, hasHeaderOverride, setCsvData, toast]);

  const handleFileRead = (file: File) => {
    // 1. File Size Limit Check
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMb = (file.size / (1024 * 1024)).toFixed(2);
      const errorMsg = `File size exceeds ${MAX_FILE_SIZE_MB} MB limit ("${file.name}" is ${fileSizeMb} MB).`;
      setError(errorMsg);
      toast.error(
        'File Size Limit Exceeded',
        `"${file.name}" (${fileSizeMb} MB) exceeds the maximum allowed file size of ${MAX_FILE_SIZE_MB} MB.`,
        {
          details: [
            `File: ${file.name}`,
            `Size: ${fileSizeMb} MB (${file.size.toLocaleString()} bytes)`,
            `Maximum Limit: ${MAX_FILE_SIZE_MB}.00 MB`,
            'Tip: Split very large datasets into smaller batches or remove unneeded columns.'
          ],
          duration: 8000
        }
      );
      return;
    }

    // 2. Empty File Check (0 Bytes)
    if (file.size === 0) {
      const errorMsg = `The selected file "${file.name}" is completely empty (0 bytes).`;
      setError(errorMsg);
      toast.error(
        'Empty File Uploaded',
        `"${file.name}" contains 0 bytes. Please select a spreadsheet with header columns and data rows.`,
        {
          details: [
            `File Name: ${file.name}`,
            'Size: 0 Bytes',
            'Make sure you saved your spreadsheet before uploading.'
          ]
        }
      );
      return;
    }

    // 3. File Extension / Format Check
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    const isStandardExt = ALLOWED_EXTENSIONS.includes(ext);
    if (!isStandardExt && file.type && !file.type.includes('text') && !file.type.includes('csv') && !file.type.includes('tab')) {
      toast.warning(
        'Uncommon File Extension',
        `"${file.name}" has extension "${ext}". Attempting to parse as delimited text...`,
        {
          details: [
            `Supported Extensions: ${ALLOWED_EXTENSIONS.join(', ')}`,
            'If this file contains binary or image data, please export as CSV or TSV first.'
          ]
        }
      );
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text || text.trim().length === 0) {
        setError('File contains no textual data.');
        toast.error(
          'File Contains No Text',
          `"${file.name}" has no readable text content after reading.`,
          {
            details: [`File: ${file.name}`, 'Content: Blank whitespace']
          }
        );
        return;
      }
      // Reset selections to auto on new file upload
      setSelectedDelimiter('auto');
      setSelectedHeaderRow(-99);
      processTextContent(text, file.name, 'auto', -99, true);
    };

    reader.onerror = () => {
      const errorMsg = `Unable to read "${file.name}" from your storage device.`;
      setError(errorMsg);
      toast.error(
        'File Upload Failed',
        `Failed to read "${file.name}" from disk. The file may be in use or permissions are restricted.`,
        {
          details: [
            `File: ${file.name}`,
            `Reader Status: Error code ${reader.error?.name || 'READ_ERROR'}`,
            `Message: ${reader.error?.message || 'I/O read failure'}`
          ],
          duration: 8000
        }
      );
    };

    reader.onabort = () => {
      toast.warning('Upload Aborted', `Reading of "${file.name}" was cancelled.`);
    };

    reader.readAsText(file, 'utf-8');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleFileRead(file);

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileRead(file);
    }
  };

  const handleFormatChange = ({ delimiter, headerRowIndex, hasHeader }: { delimiter?: string; headerRowIndex?: number; hasHeader?: boolean }) => {
    const newDelimiter = delimiter !== undefined ? delimiter : selectedDelimiter;
    const newHeaderRow = headerRowIndex !== undefined ? headerRowIndex : selectedHeaderRow;
    const newHasHeader = hasHeader !== undefined ? hasHeader : (headerRowIndex === -1 ? false : hasHeaderOverride);

    setSelectedDelimiter(newDelimiter);
    setSelectedHeaderRow(newHeaderRow);
    setHasHeaderOverride(newHasHeader);

    if (rawFileText && fileName) {
      processTextContent(rawFileText, fileName, newDelimiter, newHeaderRow, newHasHeader, false);
      toast.info(
        'Format Settings Updated',
        `Applied delimiter "${newDelimiter === 'auto' ? 'Auto' : newDelimiter}" and header mode (${newHasHeader === false || newHeaderRow === -1 ? 'No Headers' : newHeaderRow === -99 ? 'Auto' : `Row ${newHeaderRow + 1}`}).`,
        { duration: 2500 }
      );
    }
  };

  const handleLoadSample = (sample: typeof SAMPLE_DATASETS[0]) => {
    setFileName(sample.name);
    const generatedRawCsv = formatCsvString(sample.data.headers, sample.data.rows, sample.formatMeta.delimiter || ',');
    setRawFileText(generatedRawCsv);
    setFormatMeta(sample.formatMeta);
    setSelectedDelimiter('auto');
    setSelectedHeaderRow(-99);
    setHasHeaderOverride(undefined);
    setCsvData(sample.data);
    setError(null);

    toast.success(
      'Sample Dataset Loaded',
      `Loaded "${sample.name}" with ${sample.data.rows.length} rows & ${sample.data.headers.length} columns.`,
      { duration: 3500 }
    );
  };

  const handleCreateInAppDatabase = () => {
    const initialData: CSVData = {
      headers: ['SKU', 'ProductName', 'Price', 'Barcode', 'Category', 'QrLink'],
      rows: [
        { SKU: 'PROD-001', ProductName: 'Custom Sticker Item 1', Price: '10.00', Barcode: '8901234567891', Category: 'General', QrLink: 'https://example.com/p1' },
        { SKU: 'PROD-002', ProductName: 'Custom Sticker Item 2', Price: '15.50', Barcode: '8901234567907', Category: 'General', QrLink: 'https://example.com/p2' },
        { SKU: 'PROD-003', ProductName: 'Custom Sticker Item 3', Price: '20.00', Barcode: '8901234567914', Category: 'General', QrLink: 'https://example.com/p3' },
        { SKU: 'PROD-004', ProductName: 'Custom Sticker Item 4', Price: '25.00', Barcode: '8901234567921', Category: 'General', QrLink: 'https://example.com/p4' },
      ]
    };
    setCsvData(initialData);
    setFileName('InApp_Database.csv');
    toast.success('In-App Database Created', 'Created interactive database table. Add rows, add columns, and edit cells directly in the app.');
  };

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-8 pb-12">
      {/* Step Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Data Source &amp; Validation</h2>
          <p className="text-zinc-400 text-sm mt-1">
            Upload CSV, TSV, or TXT spreadsheets with auto-detected delimiters &amp; headers, generate sequences, or inspect validation metrics.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowTemplateModal(true)}
            id="btn-download-template"
            className="px-3.5 py-2 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800 text-zinc-300 text-xs font-semibold rounded-md shadow-2xs transition-colors cursor-pointer flex items-center gap-2"
            title="Download formatted sample CSV template"
          >
            <FileDown className="w-4 h-4 text-blue-600" />
            Download Template
          </button>

          {csvData && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3.5 py-2 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-xs font-semibold rounded-md shadow-2xs transition-colors cursor-pointer flex items-center gap-2"
              >
                <UploadCloud className="w-4 h-4 text-blue-600" />
                Replace File
              </button>
              <button
                onClick={() => setShowGenerator(true)}
                className="px-3.5 py-2 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-xs font-semibold rounded-md shadow-2xs transition-colors cursor-pointer flex items-center gap-2"
              >
                <Plus className="w-4 h-4 text-blue-600" />
                Add Column
              </button>
            </>
          )}
        </div>
      </div>

      {/* Upload & Generator Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" id="tour-data-upload">
        <div 
          className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-md transition-all cursor-pointer group shadow-2xs ${
            isDragging 
              ? 'border-blue-600 bg-blue-50 scale-[1.01]' 
              : 'border-zinc-700 bg-zinc-900 hover:bg-blue-50/50 hover:border-orange-400'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="w-10 h-10 rounded-md bg-blue-100/70 text-blue-600 flex items-center justify-center mb-2.5 group-hover:scale-105 transition-transform">
            <UploadCloud className="w-5 h-5" />
          </div>
          <span className="text-sm font-bold text-zinc-200 group-hover:text-blue-700 transition-colors text-center">
            {isDragging ? 'Drop CSV File Here' : 'Upload CSV / TSV File'}
          </span>
          <span className="text-[11px] text-zinc-500 mt-1 text-center">
            Auto-detects delimiters &amp; header rows
          </span>
          <input 
            type="file" 
            accept=".csv,.tsv,.tab,.txt,text/csv,text/tab-separated-values,text/plain" 
            className="hidden" 
            ref={fileInputRef} 
            onChange={handleFileUpload}
          />
        </div>

        <div 
          className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-zinc-700 rounded-md bg-zinc-900 hover:bg-blue-950/40 hover:border-blue-500 transition-all cursor-pointer group shadow-2xs"
          onClick={handleCreateInAppDatabase}
        >
          <div className="w-10 h-10 rounded-md bg-blue-900/60 text-blue-400 flex items-center justify-center mb-2.5 group-hover:scale-105 transition-transform">
            <Plus className="w-5 h-5" />
          </div>
          <span className="text-sm font-bold text-zinc-200 group-hover:text-blue-400 transition-colors text-center">
            Create In-App Database / Table
          </span>
          <span className="text-[11px] text-zinc-500 mt-1 text-center">No CSV file required — edit rows &amp; columns live</span>
        </div>
        
        <div 
          className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-zinc-700 rounded-md bg-zinc-900 hover:bg-emerald-50/50 hover:border-emerald-400 transition-all cursor-pointer group shadow-2xs"
          onClick={() => setShowGenerator(true)}
        >
          <div className="w-10 h-10 rounded-md bg-emerald-100/70 text-blue-600 flex items-center justify-center mb-2.5 group-hover:scale-105 transition-transform">
            <Database className="w-5 h-5" />
          </div>
          <span className="text-sm font-bold text-zinc-200 group-hover:text-emerald-700 transition-colors text-center">
            Generate Dynamic Sequences
          </span>
          <span className="text-[11px] text-zinc-500 mt-1 text-center">Sequential IDs, EAN-13, Expiry Dates</span>
        </div>
      </div>

      {/* Parse Error Notification */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Phase 2: In-App Variable Generator & Advanced Barcoding Modal */}
      <MacWindow 
        isOpen={showGenerator} 
        onClose={() => setShowGenerator(false)} 
        title="In-App Data Automation & Barcode Studio"
        icon={<Database className="w-5 h-5 text-blue-500" />}
        maxWidth="max-w-3xl"
      >
        <div className="flex flex-col w-full h-full max-h-[85vh]">
          <div className="p-4 overflow-y-auto flex-1">
            <InAppVariableGeneratorPanel onClose={() => setShowGenerator(false)} />
          </div>

          <div className="p-3 border-t border-zinc-800 bg-zinc-950 flex justify-end shrink-0">
            <button
              type="button"
              onClick={() => setShowGenerator(false)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
            >
              Close Studio
            </button>
          </div>
        </div>
      </MacWindow>

      {/* Download Template Reference Modal */}
      <MacWindow 
        isOpen={showTemplateModal} 
        onClose={() => setShowTemplateModal(false)} 
        title="Download CSV Format Template"
        icon={<FileDown className="w-5 h-5 text-blue-500" />}
        maxWidth="max-w-2xl"
      >
        <div className="flex flex-col w-full h-full max-h-[85vh]">
          <div className="px-6 py-4 border-b border-zinc-800 bg-zinc-950/50">
            <p className="text-xs text-zinc-400">Choose a ready-to-use spreadsheet template with common variable data headers</p>
          </div>
          <div className="p-6 overflow-y-auto flex-1 flex flex-col gap-4">
            {/* Active Template Tailored Option */}
              <div className="p-4 rounded-md border-2 border-blue-900 bg-blue-950/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-blue-400 shrink-0" />
                    <h4 className="font-bold text-sm text-zinc-200">{activeTemplatePreset.name}</h4>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-950 text-blue-300 border border-blue-800">Recommended</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">
                    {activeTemplatePreset.description}
                  </p>
                  <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                    {activeTemplatePreset.headers.map(h => (
                      <span key={h} className="px-2 py-0.5 bg-zinc-900 border border-blue-900/60 text-blue-400 rounded-md text-[11px] font-semibold font-mono">
                        {h}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex sm:flex-col items-center gap-2 shrink-0">
                  <button
                    onClick={() => {
                      handleDownloadTemplate(activeTemplatePreset);
                    }}
                    className="w-full px-4 py-2 bg-[#1473E6] hover:bg-blue-700 text-white rounded-md text-xs font-bold transition-colors shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download CSV
                  </button>
                </div>
              </div>

              <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mt-2">
                Industry-Standard Reference Templates
              </div>

              {/* Standard Templates List */}
              <div className="grid grid-cols-1 gap-3">
                {TEMPLATE_PRESETS.map((preset) => (
                  <div
                    key={preset.id}
                    className="p-4 rounded-md border border-zinc-800 hover:border-zinc-700 bg-zinc-900 hover:bg-zinc-800/60 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div className="flex-1">
                      <h4 className="font-bold text-sm text-zinc-200">{preset.name}</h4>
                      <p className="text-xs text-zinc-500 mt-0.5">{preset.description}</p>
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                        {preset.headers.map(h => (
                          <span key={h} className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded-md text-[11px] font-mono">
                            {h}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => handleDownloadTemplate(preset)}
                        className="px-3.5 py-2 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-300 rounded-md text-xs font-semibold transition-colors shadow-2xs flex items-center gap-1.5 cursor-pointer"
                      >
                        <Download className="w-3.5 h-3.5 text-zinc-500" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between shrink-0">
              <span className="text-xs text-zinc-500">Files are encoded with UTF-8 BOM for full Microsoft Excel compatibility.</span>
              <button 
                onClick={() => setShowTemplateModal(false)} 
                className="px-4 py-2 text-zinc-400 font-semibold hover:bg-zinc-700 rounded-md text-xs transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
        </div>
      </MacWindow>

      {/* Main Content Area: Validation Panel when CSV exists, or Sample Presets when Empty */}
      {csvData ? (
        <CsvValidationPanel
          report={validationReport}
          csvData={csvData}
          template={template}
          onUpdateCsv={setCsvData}
          onProceed={onNext}
          formatMeta={formatMeta}
          selectedDelimiter={selectedDelimiter}
          selectedHeaderRow={selectedHeaderRow}
          hasHeaderOverride={hasHeaderOverride}
          onFormatChange={handleFormatChange}
          fileName={fileName}
        />
      ) : (
        <div className="flex flex-col gap-6 mt-2">
          {/* Format Reference Helper Box */}
          <div className="p-4 rounded-md bg-blue-950/40 border border-blue-900/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-[#1473E6] text-white flex items-center justify-center shrink-0 shadow-xs">
                <FileDown className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-zinc-100">Need a blank or pre-filled CSV format reference?</h4>
                <p className="text-xs text-zinc-300 mt-0.5 leading-relaxed">
                  Download a template containing standard variable column headers (<code className="bg-zinc-900 border border-blue-900/60 px-1 py-0.5 rounded text-blue-300 font-mono text-[11px]">SKU</code>, <code className="bg-zinc-900 border border-blue-900/60 px-1 py-0.5 rounded text-blue-300 font-mono text-[11px]">Barcode</code>, <code className="bg-zinc-900 border border-blue-900/60 px-1 py-0.5 rounded text-blue-300 font-mono text-[11px]">Price</code>, <code className="bg-zinc-900 border border-blue-900/60 px-1 py-0.5 rounded text-blue-300 font-mono text-[11px]">QrLink</code>) or tailored to your current template.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowTemplateModal(true)}
              className="px-4 py-2 bg-[#1473E6] hover:bg-blue-700 text-white text-xs font-bold rounded-md shadow-xs transition-colors flex items-center gap-1.5 shrink-0 cursor-pointer"
            >
              <Download className="w-4 h-4" />
              Download Template
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm font-bold text-zinc-300 uppercase tracking-wider">
            <Sparkles className="w-4 h-4 text-amber-500" />
            Or Load a Pre-built Sample Dataset Directly
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" id="tour-sample-datasets">
            {SAMPLE_DATASETS.map((preset) => (
              <div
                key={preset.name}
                onClick={() => handleLoadSample(preset)}
                className="p-5 bg-zinc-900 border border-zinc-800 hover:border-orange-400 hover:shadow-md rounded-md cursor-pointer transition-all flex flex-col justify-between group"
              >
                <div>
                  <div className="w-10 h-10 rounded-md bg-zinc-800 group-hover:bg-blue-100 text-zinc-300 group-hover:text-blue-600 flex items-center justify-center mb-3 transition-colors">
                    <FileSpreadsheet className="w-5 h-5" />
                  </div>
                  <h4 className="font-bold text-sm text-zinc-200 group-hover:text-blue-600 transition-colors">
                    {preset.name}
                  </h4>
                  <p className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                    {preset.description}
                  </p>
                </div>

                <div className="mt-4 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-400">
                  <span>{preset.data.rows.length} Records</span>
                  <span className="font-semibold text-blue-600 group-hover:underline">Load Sample →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

